import { MaterialIcon } from '@gravitate-js/excalibrr'
import type { MenuProps } from 'antd'
import { Dropdown, message } from 'antd'
import React, { useState } from 'react'
import type { InvoiceGroup } from '../../../../../../api/useFreight/types'
import { DownOutlined } from '@ant-design/icons'
import api from '../../../../../../utils/api'
import { downloadBlobAndLink } from '../../../../../../api/utils'

export function InvoicePdfButton({ invoiceGroup }: { invoiceGroup: InvoiceGroup }) {
  const [loading, setLoading] = useState(false)

  const downloadSingleInvoicePdf = async (invoiceNumber: string) => {
    setLoading(true)
    try {
      const response = await api.blob('freight/invoice/download_pdf', undefined, undefined, {
        invoice_number: invoiceNumber,
        invoice_group_id: invoiceGroup.id,
      })

      downloadBlobAndLink(
        response,
        `invoice_${invoiceNumber}.pdf`,
        `Invoice #${invoiceNumber} PDF downloaded successfully`
      )
    } catch (error) {
      message.error(`Failed to generate invoice #${invoiceNumber} PDF`)
      return false
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadAll = async () => {
    if (!invoiceGroup.invoices || invoiceGroup.invoices.length === 0) {
      message.error('No invoices available to download')
      return
    }

    setLoading(true)
    try {
      const results = await Promise.all(
        invoiceGroup.invoices.map((invoice) => downloadSingleInvoicePdf(invoice.invoice_number))
      )

      const successCount = results.filter(Boolean).length

      if (successCount > 0) {
        message.success(`${successCount} of ${invoiceGroup.invoices.length} invoice PDFs downloaded successfully`)
      } else {
        message.error('Failed to download any invoice PDFs')
      }
    } catch (error) {
      console.error('Error downloading all PDFs:', error)
      message.error('Failed to download all invoice PDFs')
    } finally {
      setLoading(false)
    }
  }

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === 'all') {
      await handleDownloadAll()
    } else {
      await downloadSingleInvoicePdf(e.key)
    }
  }

  // Sort invoices with newest (highest invoice number) at the top
  const sortedInvoices = [...(invoiceGroup.invoices || [])].sort((a, b) =>
    b.invoice_number.localeCompare(a.invoice_number)
  )

  const menuItems: MenuProps['items'] = [
    ...sortedInvoices.map((invoice) => ({
      key: invoice.invoice_number,
      label: `Invoice #${invoice.invoice_number}`,
      icon: <MaterialIcon icon='description' style={{ fontSize: '16px' }} />,
    })),
    {
      type: 'divider',
    },
    {
      key: 'all',
      label: 'Download All',
      icon: <MaterialIcon icon='download_for_offline' style={{ fontSize: '16px' }} />,
    },
  ]

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  }

  const handleButtonClick = () => {
    // If there's only one invoice, download it directly
    if (sortedInvoices.length) {
      downloadSingleInvoicePdf(sortedInvoices[0].invoice_number)
    }
  }

  return (
    <Dropdown.Button
      menu={menuProps}
      onClick={handleButtonClick}
      loading={loading}
      style={{ width: '100%' }}
      icon={<DownOutlined />}
      buttonsRender={([leftButton, rightButton]) => [
        React.cloneElement(leftButton as React.ReactElement, {
          style: { flex: 1, fontWeight: 'bold' },
          icon: <MaterialIcon icon='description' className='pt-1' />,
        }),
        rightButton,
      ]}
    >
      Invoice PDF
    </Dropdown.Button>
  )
}
